import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { AlertTriangle, Clock3, Download, Plus, RotateCcw } from 'lucide-react';
import { Badge, Button, PageHeader, Spinner } from '../components/ui';
import ContentUploadDialog from '../components/ingestion/ContentUploadDialog';
import DraftRecordDrawer from '../components/ingestion/DraftRecordDrawer';
import MappingStep from '../components/ingestion/MappingStep';
import PublicationHistory from '../components/ingestion/PublicationHistory';
import PublishDialog from '../components/ingestion/PublishDialog';
import ReviewTable from '../components/ingestion/ReviewTable';
import {
  contentIngestionService,
  type ContentArea,
  type DraftChange,
  type IngestionDashboard,
  type IngestionRun,
  type PublicationSelection,
} from '../services/contentIngestionService';

const AREAS: Array<{ key: ContentArea; label: string }> = [
  { key: 'schedule', label: 'Schedule' },
  { key: 'speakers', label: 'Speakers' },
  { key: 'papers', label: 'Papers' },
  { key: 'attendees', label: 'Attendees' },
  { key: 'exhibitors', label: 'Exhibitors' },
];

function readinessLabel(dashboard: IngestionDashboard, area: ContentArea) {
  const readiness = dashboard.readiness[area];
  const errors = Number(readiness?.error_count ?? readiness?.errors ?? 0);
  const draft = dashboard.area_counts[area].draft_count;
  if (errors > 0) return { label: 'Needs review', variant: 'danger' as const };
  if (readiness?.can_publish || (readiness?.state === 'ready' && draft > 0)) return { label: 'Ready', variant: 'success' as const };
  if (draft > 0) return { label: 'In review', variant: 'warning' as const };
  return { label: 'No draft', variant: 'default' as const };
}

export default function EventContentSetupPage() {
  const { eventId = '' } = useParams<{ eventId: string }>();
  const [dashboard, setDashboard] = useState<IngestionDashboard | null>(null);
  const [activeRun, setActiveRun] = useState<IngestionRun | null>(null);
  const [drafts, setDrafts] = useState<DraftChange[]>([]);
  const [editingDraft, setEditingDraft] = useState<DraftChange | null>(null);
  const [reviewArea, setReviewArea] = useState<'schedule' | 'speakers'>('schedule');
  const [publishSelection, setPublishSelection] = useState<PublicationSelection | null>(null);
  const [panel, setPanel] = useState<'dashboard' | 'upload' | 'mapping'>('dashboard');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    setError(null);
    try {
      const next = await contentIngestionService.dashboard(eventId);
      setDashboard(next);
      setDrafts(next.enabled ? await contentIngestionService.drafts(eventId) : []);
    } catch (cause) {
      setDashboard(null);
      setError(cause instanceof Error ? cause.message : 'Unable to load content setup');
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => { void load(); }, [load]);

  async function continueRun() {
    if (!dashboard?.newest_resumable_run) return;
    setBusy(true);
    setError(null);
    try {
      const run = await contentIngestionService.run(eventId, dashboard.newest_resumable_run.id);
      setActiveRun(run);
      setPanel(run.state === 'mapping_required' || run.state === 'uploaded' || run.state === 'failed' ? 'mapping' : 'dashboard');
      if (!['mapping_required', 'uploaded', 'failed'].includes(run.state)) await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Unable to resume this run');
    } finally {
      setBusy(false);
    }
  }

  async function enable() {
    setBusy(true);
    setError(null);
    try {
      await contentIngestionService.updateSettings(eventId, true);
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Unable to enable content ingestion');
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <div className="flex justify-center p-16"><Spinner className="h-6 w-6 text-sky-400" /></div>;
  if (error && !dashboard) return <div className="p-8"><p role="alert" className="rounded-xl border border-red-500/30 bg-red-500/5 p-4 text-sm text-red-300">{error}</p></div>;
  if (!dashboard) return null;

  const isAdmin = dashboard.effective_role === 'admin';
  const reviewDrafts = drafts.filter((change) => change.entity_type === (reviewArea === 'schedule' ? 'session' : 'speaker'));

  if (!dashboard.enabled) return <div className="p-8">
    <PageHeader title="Content setup" description="Upload, review, and publish event content." />
    <section className="max-w-2xl rounded-2xl border border-amber-500/25 bg-amber-500/5 p-6">
      <AlertTriangle className="text-amber-400" size={22} />
      <h2 className="mt-3 font-semibold text-white">Content ingestion is disabled for this event</h2>
      <p className="mt-1 text-sm text-gray-400">Your workspace controls whether organizers can use the flexible ingestion workflow.</p>
      {isAdmin && <Button className="mt-4" loading={busy} onClick={() => void enable()}>Enable for this event</Button>}
      {error && <p role="alert" className="mt-3 text-sm text-red-300">{error}</p>}
    </section>
  </div>;

  if (panel === 'mapping' && activeRun) return <div className="p-8">
    <button type="button" className="mb-4 text-sm text-sky-400 hover:text-sky-300" onClick={() => setPanel('dashboard')}>← Back to content setup</button>
    <MappingStep eventId={eventId} run={activeRun} onReadyForReview={() => { setPanel('dashboard'); setActiveRun(null); void load(); }} />
  </div>;

  return <div className="p-8">
    <PageHeader
      title="Content setup"
      description="Upload, map, review, and publish event content without changing the live attendee experience until approval."
      action={<div className="flex flex-wrap gap-2">
        <Button variant="secondary" icon={<Download size={14} />} onClick={() => void contentIngestionService.downloadTemplate(eventId)}>Download standard template</Button>
        <Button icon={<Plus size={14} />} onClick={() => setPanel(panel === 'upload' ? 'dashboard' : 'upload')}>Add content</Button>
      </div>}
    />
    {error && <p role="alert" className="mb-4 rounded-xl border border-red-500/30 bg-red-500/5 p-3 text-sm text-red-300">{error}</p>}
    {panel === 'upload' && <div className="mb-6"><ContentUploadDialog eventId={eventId} limits={dashboard.limits} onRunCreated={(run) => { setActiveRun(run); setPanel('mapping'); }} /></div>}
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
      {AREAS.map(({ key, label }) => {
        const counts = dashboard.area_counts[key];
        const status = readinessLabel(dashboard, key);
        return <section key={key} aria-label={`${label} readiness`} className="rounded-2xl border border-gray-800 bg-gray-900 p-4">
          <div className="flex items-center justify-between gap-2"><h2 className="font-medium text-white">{label}</h2><Badge variant={status.variant}>{status.label}</Badge></div>
          <div className="mt-4 space-y-1 text-sm text-gray-400"><p>{counts.published_count} published</p><p>{counts.draft_count} draft</p></div>
        </section>;
      })}
    </div>
    <section className="mt-6 rounded-2xl border border-gray-800 bg-gray-900 p-5">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-medium text-white">Current work</h2>
          {dashboard.newest_resumable_run ? <p className="mt-1 flex items-center gap-2 text-sm text-gray-400"><Clock3 size={14} /> Last activity {new Date(dashboard.newest_resumable_run.updated_at).toLocaleString()}</p> : <p className="mt-1 text-sm text-gray-500">No unfinished ingestion run.</p>}
        </div>
        {dashboard.newest_resumable_run && <div className="flex gap-2">
          <Button variant="secondary" icon={<Download size={14} />} onClick={() => void contentIngestionService.downloadErrors(eventId, dashboard.newest_resumable_run!.id)}>Download errors</Button>
          <Button icon={<RotateCcw size={14} />} loading={busy} onClick={() => void continueRun()}>Continue review</Button>
        </div>}
      </div>
    </section>
    {drafts.length > 0 && <section className="mt-6 rounded-2xl border border-gray-800 bg-gray-900 p-5">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3"><div><h2 className="font-medium text-white">Draft review</h2><p className="mt-1 text-sm text-gray-400">Review proposed values. Draft changes remain invisible to attendees until an admin publishes them.</p></div>{isAdmin && <div className="flex gap-2"><Button variant="secondary" onClick={() => setPublishSelection({ areas: [reviewArea] })}>Publish {reviewArea}</Button><Button onClick={() => setPublishSelection({ all_ready: true })}>Publish all ready changes</Button></div>}</div>
      <div className="mb-4 flex gap-2"><Button size="sm" variant={reviewArea === 'schedule' ? 'primary' : 'secondary'} onClick={() => setReviewArea('schedule')}>Schedule drafts</Button><Button size="sm" variant={reviewArea === 'speakers' ? 'primary' : 'secondary'} onClick={() => setReviewArea('speakers')}>Speaker drafts</Button></div>
      <ReviewTable eventId={eventId} changes={reviewDrafts} onEdit={setEditingDraft} onChanged={load} />
    </section>}
    {isAdmin && <section id="publication-history" className="mt-6 rounded-2xl border border-gray-800 bg-gray-900 p-5">
      <h2 className="font-medium text-white">Publication history</h2><p className="mb-4 mt-1 text-sm text-gray-400">Every publication is immutable. Rollbacks create a new draft for approval.</p>
      <PublicationHistory eventId={eventId} onRollbackCreated={() => void load()} />
    </section>}
    <DraftRecordDrawer eventId={eventId} change={editingDraft} onClose={() => setEditingDraft(null)} onSaved={() => { setEditingDraft(null); void load(); }} />
    <PublishDialog open={publishSelection !== null} eventId={eventId} selection={publishSelection ?? { all_ready: true }} onClose={() => setPublishSelection(null)} onPublished={load} />
  </div>;
}
